'use client'

import { useEffect, useState } from 'react'
import type { AlertColor } from '@mui/material/Alert'
import type { FraudHeaderValidationStatus, FraudHeaderValidationSummary } from '../types'
import MUIAlert from '@mui/material/Alert'
import MUIAlertTitle from '@mui/material/AlertTitle'
import MUIButton from '@mui/material/Button'
import MUICollapse from '@mui/material/Collapse'
import MUISnackbar from '@mui/material/Snackbar'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export type FraudHeaderValidationToastProps = {
  validation: FraudHeaderValidationSummary | null
}

const SEVERITY_BY_STATUS: Record<FraudHeaderValidationStatus, AlertColor> = {
  PASS: 'success',
  WARNINGS: 'warning',
  ERRORS: 'error',
  UNKNOWN: 'info'
}

const TITLE_BY_STATUS: Record<FraudHeaderValidationStatus, string> = {
  PASS: 'Fraud-prevention headers: PASS',
  WARNINGS: 'Fraud-prevention headers: WARNINGS',
  ERRORS: 'Fraud-prevention headers: ERRORS',
  UNKNOWN: 'Fraud-prevention headers: validation feedback unavailable'
}

// RF14 (FPH-ACC11 contract): non-prod-only toast rendered after an ITSA submit resolves,
// showing HMRC's fraud-prevention-header validation verdict. `validation` is null in
// production (the server-side guard is the actual control here — this toast simply renders
// nothing when the field is absent) and non-null in non-prod. UNKNOWN covers both an
// explicit HMRC UNKNOWN verdict and the feedback-capture timeout/failure case (RF14) —
// there is no separate crash/error rendering, by design.
export const FraudHeaderValidationToast = ({ validation }: FraudHeaderValidationToastProps) => {
  const [open, setOpen] = useState(false)
  const [detailExpanded, setDetailExpanded] = useState(false)

  // A fresh validation object (new submit) re-opens the toast even if the previous one was
  // dismissed, and always starts with the per-header detail collapsed.
  useEffect(() => {
    if (validation) {
      setOpen(true)
      setDetailExpanded(false)
    }
  }, [validation])

  if (!validation) return null

  const severity = SEVERITY_BY_STATUS[validation.status]
  const title = TITLE_BY_STATUS[validation.status]
  const headers = validation.headers || []

  return (
    <MUISnackbar
      open={open}
      onClose={() => setOpen(false)}
      autoHideDuration={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      data-testid="FraudHeaderValidationToast"
    >
      <MUIAlert
        severity={severity}
        onClose={() => setOpen(false)}
        data-testid="FraudHeaderValidationToastAlert"
        sx={{ width: '100%', maxWidth: 480 }}
      >
        <MUIAlertTitle>{title}</MUIAlertTitle>
        {validation.status === 'UNKNOWN' && (
          <MUITypography variant="body2">
            Fraud-prevention header validation feedback unavailable.
          </MUITypography>
        )}
        {headers.length > 0 && (
          <MUIStack gap={0.5}>
            <MUIButton
              size="small"
              onClick={() => setDetailExpanded((prev) => !prev)}
              data-testid="FraudHeaderValidationToastToggle"
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
            >
              {detailExpanded ? 'Hide header detail' : `Show header detail (${headers.length})`}
            </MUIButton>
            <MUICollapse in={detailExpanded} timeout="auto" unmountOnExit>
              <MUIStack gap={1} data-testid="FraudHeaderValidationToastDetail">
                {headers.map((header) => (
                  <MUIStack key={header.headerName}>
                    <MUITypography variant="body2" sx={{ fontWeight: 700 }}>
                      {header.headerName}: {header.verdict}
                    </MUITypography>
                    <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                      {header.message}
                    </MUITypography>
                  </MUIStack>
                ))}
              </MUIStack>
            </MUICollapse>
          </MUIStack>
        )}
      </MUIAlert>
    </MUISnackbar>
  )
}
